import { Grid, Stack, Typography } from '@mui/material';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { appointmentTypeEnums } from '../../../utils/constants';
import { isValid, differenceInYears } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { CallListTabs } from '../../../store/onboardingWorkflow.slice';

const CallItemConfirmationInfo = () => {
    const { callItem, confirmationCallItem, selectedCallListTab } = useSelector((state: RootState) => state.onboardingWorkflowSlice);
    const { activeProviders } = useSelector((state: RootState) => state.provider);

    const item = selectedCallListTab === CallListTabs.Confirmations ? confirmationCallItem : callItem;

    const DOB = formatDate(item?.DateOfBirth, 'MM-dd-yyyy');

    const address = `${item.Address ? item.Address + ',' : ''} ${item.Address2 ? item.Address2 + ',' : ''} ${item.City ? item.City + ',' : ''} ${item.State ? item.State + ',' : ''
        } ${item.Zip ? item.Zip : ''}`;

    const startDate = confirmationCallItem?.AppointmentDateTime ? new Date(confirmationCallItem?.AppointmentDateTime) : '';
    const endDate = confirmationCallItem?.AppointmentEndDateTime ? new Date(confirmationCallItem?.AppointmentEndDateTime) : '';

    return (
        <>
            <Grid container spacing={1}>
                <Grid p={1} size={{ xs: 12, sm: 6, md: 3 }} borderRight={1} borderColor={'gray'}>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} borderBottom={1}>
                        <Typography variant="body2">AHCCCS ID:</Typography>
                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                            {item?.AHCCCSID || ''}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2">Last Name:</Typography>
                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                            {item?.LastName || ''}
                        </Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2">First Name:</Typography>
                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                            {item?.FirstName || ''}
                        </Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'space-between'} spacing={2} marginTop={1} borderBottom={1}>
                        <Typography variant="body2">DOB:</Typography>
                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                            {`${DOB || ''} ${' (Age: ' + differenceInYears(new Date(), toDate(item?.DateOfBirth)) + ')' || ''}`}
                        </Typography>
                    </Stack>
                </Grid>

                <Grid p={1} size={{ xs: 12, sm: 6, md: 3 }} borderRight={1} borderColor={'gray'}>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Appt Address:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {item.AppointmentAddress || ''}
                        </Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Main Address:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {address || ''}
                        </Typography>
                    </Stack>
                    {item.IsDifferMeetingAddress && (
                        <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                            <Typography variant="body2" marginRight={'auto'} textAlign={'left'}>
                                Different Meeting Address:
                            </Typography>
                            <Typography fontWeight={700} variant="body2" textAlign={'right'}>
                                {item.MeetingAddress?.FullAddress?.toUpperCase() || ''}
                            </Typography>
                        </Stack>
                    )}
                </Grid>

                <Grid p={1} size={{ xs: 12, sm: 6, md: 3 }} borderRight={1} borderColor={'gray'}>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginBottom={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Eligibility:
                        </Typography>
                        <Typography fontWeight={700} variant="body2">
                            {item?.Eligibility || ''}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Status:
                        </Typography>
                        <Typography fontWeight={700} variant="body2">
                            {MemberStatusEnum[item?.MemberStatus] || ''}
                        </Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Health Plan:
                        </Typography>
                        <Typography fontWeight={700} variant="body2" textAlign={'right'}>
                            {item?.HealthPlan || ''}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Population Name:
                        </Typography>
                        <Typography fontWeight={700} variant="body2">
                            {item?.PopulationName || ''}
                        </Typography>
                    </Stack>
                </Grid>

                <Grid p={1} size={{ xs: 12, sm: 6, md: 3 }}>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Provider:
                        </Typography>

                        {item?.ProviderNames?.map((id, index) => (
                            <Typography key={`${id}_${index}`} variant="body2" fontWeight={700} textAlign={'right'}>
                                {activeProviders.find((provider) => provider.Id === id)?.FullName || ''}
                            </Typography>
                        )) || ''}
                    </Stack>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Appt. Type:
                        </Typography>
                        <Typography fontWeight={700} variant="body2">
                            {appointmentTypeEnums.find((el) => el.value === item.AppointmentType)?.text || ''}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'} textAlign={'start'}>
                            Appt. Date:
                        </Typography>
                        <Typography fontWeight={700} variant="body2" textAlign={'right'}>
                            {startDate ? formatDate(startDate, 'MM-dd-yyyy ') : ''}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'} textAlign={'start'}>
                            Appt. Time:
                        </Typography>
                        <Typography fontWeight={700} variant="body2" textAlign={'right'}>
                            {`${startDate ? formatDate(startDate, 'hh:mm a') : ''} - ${endDate ? formatDate(endDate, 'hh:mm a') : ''}`}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );
};
export default CallItemConfirmationInfo;
